import React, { useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
    Command, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../ui/command';
import CustomFieldBadge from '../custom-fields/CustomFieldBadge';

/**
 * ColumnPicker — the "Columns" control on the Contacts and Companies lists
 * (#1000, List Tables mockup): a searchable checklist of every column, ordered
 * standard → computed → custom with no group headings. Ticking shows a column, unticking hides it;
 * `lockedKey` (the name column) is always shown. Each item is a label with a
 * bare check on the right, like the shadcn data-table Columns menu; items are
 * A–Z within each group. Modelled on the List
 * Management picker (LmColumnPicker). Presentational: the page owns the view
 * (useTableView) and passes `visibleKeys` and the callbacks. The trigger is a
 * plain "Columns" so the list toolbar fits one line.
 */
const GROUPS = [
    { key: 'standard', test: f => !f.isCustom && !f.computed },
    { key: 'computed', test: f => f.computed },
    { key: 'custom', test: f => f.isCustom },
];

const matches = (f, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return f.label.toLowerCase().includes(needle) || f.key.toLowerCase().includes(needle);
};

function Option({ field, on, locked, onToggle }) {
    return (
        <CommandItem
            value={field.key} disabled={locked}
            onSelect={() => onToggle(field.key, !on)}
            role="option" aria-checked={on} aria-disabled={locked || undefined}
            data-testid="column-option" data-column-key={field.key} data-on={on ? 'true' : 'false'}
            className="gap-2 px-2 py-1.5 text-sm text-foreground"
        >
            <span className="min-w-0 flex-1 truncate">{field.label}</span>
            {locked ? (
                <span className="shrink-0 text-[11px] text-muted-foreground">Always shown</span>
            ) : field.isCustom && <CustomFieldBadge />}
            {/* shadcn data-table style: a bare check on the right, empty space when hidden */}
            <Check aria-hidden="true" className={`w-4 h-4 shrink-0 ${on ? 'opacity-100' : 'opacity-0'}`} />
        </CommandItem>
    );
}

export default function ColumnPicker({ allFields, visibleKeys, lockedKey, onToggle }) {
    const [query, setQuery] = useState('');
    const q = query.trim();
    const groups = useMemo(() => GROUPS.map(g => {
        const all = allFields.filter(g.test).sort((a, b) => a.label.localeCompare(b.label));
        return { ...g, all, shown: all.filter(f => matches(f, q)) };
    }).filter(g => g.all.length > 0), [allFields, q]);
    const matching = groups.reduce((n, g) => n + g.shown.length, 0);

    return (
        <Popover onOpenChange={(open) => { if (!open) setQuery(''); }}>
            <PopoverTrigger asChild>
                {/* Same look as the list toolbar's filter dropdowns, so it sits with them. */}
                <Button variant="outline" data-testid="columns-button"
                        className="h-auto gap-1.5 whitespace-nowrap px-2.5 py-1.5 text-sm font-normal border-stone-200 rounded-lg bg-white shadow-none hover:bg-stone-50">
                    Columns
                    <ChevronDown className="w-4 h-4" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[320px] max-w-[calc(100vw-24px)] p-0" data-testid="columns-panel">
                <Command shouldFilter={false} loop className="bg-popover text-popover-foreground">
                    <CommandInput value={query} onValueChange={setQuery} placeholder="Search columns..."
                                  aria-label="Search columns" data-testid="columns-search" />
                    <CommandList aria-label="Columns" aria-multiselectable="true" className="max-h-[320px] p-1">
                        {groups.filter(g => g.shown.length > 0).map(g => (
                            <CommandGroup
                                key={g.key} data-testid={`columns-group-${g.key}`}
                            >
                                {g.shown.map(f => (
                                    <Option key={f.key} field={f} on={visibleKeys.has(f.key)}
                                            locked={f.key === lockedKey} onToggle={onToggle} />
                                ))}
                            </CommandGroup>
                        ))}
                    </CommandList>
                    {matching === 0 && (
                        <div className="px-3 py-6 text-center text-[12.5px] text-muted-foreground" role="status" data-testid="columns-empty">
                            No columns match “{q}”
                        </div>
                    )}
                </Command>
            </PopoverContent>
        </Popover>
    );
}
